import React, { useRef, useEffect } from 'react';
import { classNames } from 'primereact/utils';
import { DashboardMetricKey } from '../../../common/enums';
import { MetricConfig } from '../../../types/dashboard';
import { BASIC_METRICS_KEYS, ANALYTICS_METRICS_KEYS, STORE_METRICS_KEYS } from '../../../types/dashboard';
import styles from './CustomizationDropdown.module.scss';

export interface CustomizationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  metrics: MetricConfig[];
  disabledMetrics: DashboardMetricKey[];
  onToggle: (metricKey: DashboardMetricKey) => void;
  togglingMetricKey: DashboardMetricKey | null;
}

export const CustomizationDropdown = React.memo<CustomizationDropdownProps>(({
  isOpen,
  onClose,
  metrics,
  disabledMetrics,
  onToggle,
  togglingMetricKey,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const basicMetrics = metrics.filter(metric => BASIC_METRICS_KEYS.includes(metric.key));
  const analyticsMetrics = metrics.filter(metric => ANALYTICS_METRICS_KEYS.includes(metric.key));
  const storeMetrics = metrics.filter(metric => STORE_METRICS_KEYS.includes(metric.key));

  const isMetricDisabled = (metricKey: DashboardMetricKey) => {
    return disabledMetrics.includes(metricKey);
  };

  const handleMetricToggle = (metricKey: DashboardMetricKey) => {
    onToggle(metricKey);
  };

  const renderMetricSection = (title: string, sectionMetrics: MetricConfig[]) => {
    if (sectionMetrics.length === 0) return null;

    return (
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>{title}</h4>
        <div className={styles.metricsGrid}>
          {sectionMetrics.map(metric => {
            const isDisabled = isMetricDisabled(metric.key);
            const isChecked = !isDisabled;
            const isToggling = togglingMetricKey === metric.key;

            return (
              <label
                key={metric.key}
                className={classNames(styles.metricItem, {
                  [styles.checked]: isChecked,
                  [styles.loading]: isToggling,
                })}
              >
                <div className={styles.checkbox}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => handleMetricToggle(metric.key)}
                    className={styles.checkboxInput}
                    disabled={isToggling}
                  />
                  <div className={styles.checkboxCustom}>
                    {isChecked && !isToggling && (
                      <span className={styles.checkmark}>✓</span>
                    )}
                  </div>
                </div>

                <div className={styles.metricInfo}>
                  <div className={styles.metricIcon}>
                    <span className={classNames(styles.icon, metric.iconClass)}>
                      {metric.icon}
                    </span>
                  </div>
                  <div className={styles.metricText}>
                    <span className={styles.metricTitle}>{metric.title}</span>
                    <span className={styles.metricDescription}>{metric.description}</span>
                  </div>
                </div>

                {isToggling && (
                  <div className={styles.loadingOverlay}>
                    <div className={styles.loader}></div>
                  </div>
                )}
              </label>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div 
      ref={dropdownRef}
      className={classNames(styles.dropdown, { [styles.open]: isOpen })}
    >
      <div className={styles.content}>
        {renderMetricSection('Основни метрики', basicMetrics)}
        {renderMetricSection('Магазин', storeMetrics)}
        {renderMetricSection('Анализи и показатели', analyticsMetrics)}
      </div>
    </div>
  );
});